import { action, autorun, computed, makeObservable, observable } from 'mobx';
import { DateUtils } from '@shared/utils/DateUtils';
import { Ajax } from '@shared/Ajax';
import { IFilterItem } from '@shared/ui-react/CheckboxListDropdown';
import { ProjectCodesService } from '@shared/services/ProjectCodesService';


export const LogpointsEmptyFilter: Readonly<ILogpointsFilter> = {
    fromTimestamp: null,
    toTimestamp: null,
    datePresetIndex: null,
    onlyOutOfTolerance: false,
    codes: null,
    userIds: null,
    vehicleIds: null,
    sources: null,
    approvals: null,
    hideDzPlusMinus: null,
    hideDzMode: 'outside',
    isReady: true, // filter can only be applied when possible filter values are loaded
};

export class LogpointsFilterStore implements DateRange.IFilterStore {
    @observable
    public filter: ILogpointsFilter = {
        ...LogpointsEmptyFilter,
    };

    @observable public vehicles: Map<number, IFilterItem> = new Map();
    @observable public creators: Map<number, IFilterItem> = new Map();
    @observable public codes: Map<string, IFilterItem> = new Map();
    @observable public sources: Map<string, IFilterItem> = new Map();
    @observable public approvals: Map<string, IFilterItem> = new Map([
        ['MEASURED', { value: 'MEASURED', title: __('js_logpoints.approval.measured') }],
        ['CHECKED', { value: 'CHECKED', title: __('js_logpoints.approval.checked') }],
        ['APPROVED', { value: 'APPROVED', title: __('js_logpoints.approval.approved') }],
    ]);

    @observable filterPinned = false;
    @observable filterOpened = false;

    initialVehicleName = null; // for legacy, set vehicleId filter based on name after filters are loaded

    constructor(private onChange: (state: ILogpointsFilter) => void) {
        makeObservable(this);
        this.fetch();

        setTimeout(() => {
            this.onChange(this.filter);
        }, 1);
    }

    destroy() {}

    fetch = () => {
        this.filter.isReady = false;
        return Ajax({
            url: '/api/1/logpoint/ajax_filter_users_and_vehicles_by_project.json',
        })
            .done(
                action(async (resp: { vehicles: any[]; users: any[]; codes: string[] }) => {
                    // sources
                    [
                        ['ROVER_GPS', 'logpoints.type.ROVER_GPS_js'],
                        ['TOTAL_STATION', 'logpoints.type.TOTAL_STATION_js'],
                        ['SIMULATOR', 'logpoints.type.SIMULATOR_js'],
                        ['UNKNOWN', 'logpoints.type.UNKNOWN_js'],
                        ['MANUAL', 'logpoints.type.MANUAL_js'],
                    ].forEach(source => {
                        this.sources.set(source[0], {
                            value: source[0],
                            title: __(source[1]),
                        });
                    });

                    // users
                    this.creators.set(-1, {
                        value: -1,
                        title: __('general.empty'),
                    });
                    resp.users.forEach(user => {
                        this.creators.set(user.id, {
                            value: user.id,
                            title: `${user.name}`,
                        });
                    });
                    const creatorIds = resp.users.map(user => user.id);
                    this.filter.userIds = (this.filter.userIds ?? []).filter(id => creatorIds.includes(id));

                    // codes
                    this.codes.set('-1', {
                        value: '-1',
                        title: __('general.empty'),
                    });
                    (resp.codes ?? []).forEach(code => {
                        this.codes.set(code, {
                            value: code,
                            title: code,
                        });
                    });
                    this.filter.codes = (this.filter.codes ?? []).filter(code => resp.codes.includes(code));

                    // print codes without details at first and update labels when codes are loaded
                    ProjectCodesService.fetchCodes().then(() => {
                        (resp.codes ?? []).forEach(code => {
                            this.codes.set(code, {
                                value: code,
                                title: ProjectCodesService.codeWithDescription(code),
                            });
                        });
                    });

                    // vehicles
                    this.vehicles.set(-1, {
                        value: -1,
                        title: __('general.empty'),
                    });
                    resp.vehicles.forEach(vehicle => {
                        this.vehicles.set(vehicle.id, {
                            value: vehicle.id,
                            title: `${vehicle.name}`,
                        });
                    });
                    const vehicleIds = resp.vehicles.map(vehicle => vehicle.id);
                    this.filter.vehicleIds = (this.filter.vehicleIds ?? []).filter(id => vehicleIds.includes(id));

                    if (this.initialVehicleName) {
                        const found = [...this.vehicles.values()].find(v => v.title === this.initialVehicleName);
                        if (found) {
                            this.filter.vehicleIds = [found.value as number];
                        }
                    }
                })
            )
            .always(() => {
                autorun(
                    () => {
                        this.filter.fromTimestamp;
                        this.filter.toTimestamp;
                        this.filter.onlyOutOfTolerance;
                        this.filter.codes;
                        this.filter.sources;
                        this.filter.vehicleIds;
                        this.filter.approvals;
                        this.filter.userIds;
                        this.filter.hideDzPlusMinus;
                        this.filter.hideDzMode;
                        this.onChange(this.filter);
                    },
                    { delay: 1 }
                );

                this.filter.isReady = true;
                this.onChange(this.filter);
            });
    };

    @action.bound
    public setFilter(filter: Partial<ILogpointsFilter>) {
        this.filter = {
            ...this.filter,
            ...filter,
        };
    }

    @action.bound
    public clearFilter() {
        this.filter = {
            ...LogpointsEmptyFilter,
        };
    }

    @action.bound
    public setDates(start: number, end: number, datePresetIndex: number) {
        const [safeStart, safeEnd] = DateUtils.clampDateRange(start, end);

        this.setFilter({
            fromTimestamp: safeStart,
            toTimestamp: safeEnd,
            datePresetIndex: datePresetIndex,
        });
    }

    @computed
    get numberOfAppliedFilters(): number {
        let count = 0;
        if (this.filter.onlyOutOfTolerance === true) count += 1;
        if (this.filter.codes?.length) count += 1;
        if (this.filter.userIds?.length) count += 1;
        if (this.filter.vehicleIds?.length) count += 1;
        if (this.filter.sources?.length) count += 1;
        if (this.filter.approvals?.length) count += 1;
        if (this.filter.hideDzPlusMinus != null && this.filter.hideDzPlusMinus > 0) count += 1;
        if (this.filter.fromTimestamp || this.filter.toTimestamp) count += 1;
        return count;
    }
}
